/* Vertical arithmetic of the quiz card, split out of paint.ts so node --test can pin it.
   No imports: this module is loaded by the test runner, which does not resolve bare specifiers.

   All metrics are measured off the 1000x750 artboard. Three questions have to fit between
   QUIZ_QUESTION_TOP and the foot, so the block cost below is a budget, not a preference:
   heading 24 + prompt 24 + four options 112 + gap 18 = 185 per block. Three blocks paint 537px
   (the last gap falls outside the card ink) into the 556px between y=136 and the foot at y=692,
   so the 19px left over is the wrap allowance - one prompt may run to two lines and still fit. */

export const QUIZ_CARD_INSET = 18;
export const QUIZ_TEXT_INSET = 44;
/** Top of the first question, measured from the card top. */
export const QUIZ_QUESTION_TOP = 118;
/** Foot hairline to the card bottom. */
export const QUIZ_FOOT_HEIGHT = 40;
export const QUIZ_HEADING_ADVANCE = 24;
export const QUIZ_PROMPT_LINE = 24;
export const QUIZ_PROMPT_GAP = 7;
export const QUIZ_OPTION_LINE = 22;
export const QUIZ_OPTION_GAP = 6;
export const QUIZ_QUESTION_GAP = 18;

/** Fixed cost of a block: the heading line, the prompt gap and the gap to the next block. */
export const QUIZ_BLOCK_FIXED = QUIZ_HEADING_ADVANCE + QUIZ_PROMPT_GAP + QUIZ_QUESTION_GAP;
/** A prompt past this stops being a prompt and starts being a page of its own. */
export const QUIZ_PROMPT_MAX_LINES = 4;

/** Lines a question block may spend. See `quizBlockFit`. */
export interface QuizBlockFit {
  promptLines: number;
  optionLines: number;
  /** Options actually painted; fewer than the question has means the tail is summarised. */
  optionsShown: number;
}

/** Height a block costs at those line counts, including the summary row when options are dropped. */
export function quizBlockHeight(promptLines: number, optionsShown: number, optionLines: number, optionCount: number): number {
  const rows = optionsShown + (optionsShown < optionCount ? 1 : 0);
  return QUIZ_BLOCK_FIXED
    + promptLines * QUIZ_PROMPT_LINE
    + rows * (optionLines * QUIZ_OPTION_LINE + QUIZ_OPTION_GAP);
}

/**
 * How many lines each part of a question block may use to fit `perBlock` pixels.
 *
 * The calibration is one prompt line plus four one-line options, exactly the 185px budget above.
 * A real set is not written to that budget, so the fit is computed rather than assumed - a tự luận question with no options
 * gets its spare height back as prompt lines, and a question with more options than fit sheds the
 * tail behind a count instead of painting over the card foot.
 */
export function quizBlockFit(perBlock: number, optionCount: number): QuizBlockFit {
  const optionLines = 1;
  let optionsShown = Math.max(0, optionCount);
  // shed options first: the prompt is already at its floor of one line at this point
  while (optionsShown > 0 && quizBlockHeight(1, optionsShown, optionLines, optionCount) > perBlock) optionsShown -= 1;
  let promptLines = 1;
  while (
    promptLines < QUIZ_PROMPT_MAX_LINES
    && quizBlockHeight(promptLines + 1, optionsShown, optionLines, optionCount) <= perBlock
  ) promptLines += 1;
  return { promptLines, optionLines, optionsShown };
}

/* A solo page: one question owns the whole card, so its lines are granted from what the text wants. */
export const QUIZ_SOLO_PROMPT_MAX_LINES = 16;
export const QUIZ_SOLO_OPTION_MAX_LINES = 3;
export const QUIZ_STEM_LINE = 22;
export const QUIZ_STEM_GAP = 14;
export const QUIZ_STEM_MAX_LINES = 12;

/**
 * Lines for a solo question in `height` pixels, heading included. Options keep every row first,
 * then grow toward the lines they want (statements run long), then the prompt takes what is left.
 */
export function quizSoloFit(height: number, optionCount: number, wantedPromptLines: number, wantedOptionLines: number): QuizBlockFit {
  let optionsShown = Math.max(0, optionCount);
  while (optionsShown > 0 && quizBlockHeight(1, optionsShown, 1, optionCount) > height) optionsShown -= 1;
  const optionCap = Math.min(Math.max(1, wantedOptionLines), QUIZ_SOLO_OPTION_MAX_LINES);
  let optionLines = 1;
  while (optionLines < optionCap && quizBlockHeight(1, optionsShown, optionLines + 1, optionCount) <= height) optionLines += 1;
  const promptCap = Math.min(Math.max(1, wantedPromptLines), QUIZ_SOLO_PROMPT_MAX_LINES);
  let promptLines = 1;
  while (promptLines < promptCap && quizBlockHeight(promptLines + 1, optionsShown, optionLines, optionCount) <= height) promptLines += 1;
  return { promptLines, optionLines, optionsShown };
}

/**
 * Lines a câu chùm stem may take above its child in `height` pixels: what it wants, but never so
 * many that the child cannot keep two prompt lines and one line per option.
 */
export function quizStemLines(height: number, wantedStemLines: number, wantedPromptLines: number, optionCount: number): number {
  const reserve = quizBlockHeight(Math.min(Math.max(1, wantedPromptLines), 2), optionCount, 1, optionCount);
  const room = Math.floor((height - reserve - QUIZ_STEM_GAP) / QUIZ_STEM_LINE);
  return Math.max(0, Math.min(wantedStemLines, QUIZ_STEM_MAX_LINES, room));
}
